import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Card,
  Page,
  Layout,
  Text,
  Badge,
  Button,
  Icon,
  BlockStack,
  InlineStack,
  ButtonGroup,
  Tooltip,
  Spinner,
  TextField,
} from '@shopify/polaris';
import {
  ContentMinor,
  PlayMinor,
  ViewMajor,
  RefreshMajor,
    ExternalMinor,
    ChevronLeftMinor,
    ChevronRightMinor,
} from '@shopify/polaris-icons';
import '@shopify/polaris/build/esm/styles.css';
import '../modal-overrides.css';
import '../dashboard.css';

// Import services
import { previewersService } from '../services/previewersService';

// Import modal components
import ViewPreviewModal from './modals/ViewPreviewModal';
import PreviewImageModal from './modals/PreviewImageModal';
import CreatePreviewerModal from './modals/CreatePreviewerModal';
import GenerateStoryConfirmModal from './modals/GenerateStoryConfirmModal';
import AuthenticatedAssetImage from './AuthenticatedAssetImage';

import { getServerURL } from '../services/config';
// Define SERVER_URL constant
const SERVER_URL = getServerURL();

if (!SERVER_URL) {
  console.error('REACT_APP_SERVER_URL environment variable is not set');
}


  // Add renderPreviewersContent function
  const PreviewerContentPage = ({ setToastMessage, setToastActive }) => {
    const headings = [
      'Generation ID',
      'Kid Name',
      'Kid Image',
      'Story Name',
      'Story Image',
      'Created Date', // <-- new column
      'Status',
      'Actions',
    ];

    const [previewersStatusTab, setPreviewersStatusTab] = useState('all');
    const [previewersSearchQuery, setPreviewersSearchQuery] = useState('');

    // Add state for Previewers tab
    const [previewers, setPreviewers] = useState([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [total, setTotal] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrev, setHasPrev] = useState(false);
    const [isLoadingPreviewers, setIsLoadingPreviewers] = useState(false);
    const [count, setCount] = useState(0);

      // Add state for Preview Image Modal (Previews page)
  const [isPreviewImageModalOpen, setIsPreviewImageModalOpen] = useState(false);
  const [previewModalImageUrl, setPreviewModalImageUrl] = useState('');
  const [previewModalImageAlt, setPreviewModalImageAlt] = useState('');

  // Add state for View Preview modal
  const [isViewPreviewModalOpen, setIsViewPreviewModalOpen] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState(null);

  // Add state for Create Previewer modal
  const [isCreatePreviewerModalOpen, setIsCreatePreviewerModalOpen] = useState(false);

  // Generate Story confirm modal
  const [isGenerateStoryModalOpen, setIsGenerateStoryModalOpen] = useState(false);
  const [selectedPreviewForGeneration, setSelectedPreviewForGeneration] = useState(null);


    // Fetch previewers data
    const fetchPreviewers = useCallback(async () => {
        setIsLoadingPreviewers(true);
        try {
            const result = await previewersService.getAllPreviewers({
              page,
              limit,
              status: previewersStatusTab,
              q: previewersSearchQuery || undefined,
            });
            setPreviewers(Array.isArray(result?.data) ? result.data : []);
            if (result) {
              setTotal(Number(result.total || 0));
              setPage(Number(result.page || 1));
              setLimit(Number(result.limit || limit));
              setCount(Number(result.count || (Array.isArray(result?.data) ? result.data.length : 0)));
              setPageCount(Number(result.pageCount || 0));
              setHasNext(Boolean(result.hasNext));
              setHasPrev(Boolean(result.hasPrev));
            }
        } catch (err) {
            setPreviewers([]);
            setTotal(0);
            setPage(1);
            setCount(0);
            setPageCount(0);
            setHasNext(false);
            setHasPrev(false);
        } finally {
        setIsLoadingPreviewers(false);
        }
    }, [page, limit, previewersStatusTab, previewersSearchQuery]);

    // Fetch previewers when tab is selected
    useEffect(() => {
        fetchPreviewers();
    }, [fetchPreviewers]);

    // Add event listener for Create Previewer modal
    useEffect(() => {
        const handleOpenCreatePreviewerModal = () => {
        setIsCreatePreviewerModalOpen(true);
        };

        window.addEventListener('openCreatePreviewerModal', handleOpenCreatePreviewerModal);
        
        return () => {
        window.removeEventListener('openCreatePreviewerModal', handleOpenCreatePreviewerModal);
        };
    }, []);


    // Add handler for Preview Image click
    const handlePreviewImageClick = useCallback((imageUrl, imageAlt) => {
        setPreviewModalImageUrl(imageUrl);
        setPreviewModalImageAlt(imageAlt);
        setIsPreviewImageModalOpen(true);
    }, []);

    // Add handler for Share button click
    const handleShareClick = useCallback(async (generationId) => {
        const shareUrl = `https://preview.bysnaps.ai/pages/preview?generation_id=${generationId}`;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setToastMessage('Share link copied to clipboard!');
            setToastActive(true);
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
            setToastMessage('Failed to copy link to clipboard');
            setToastActive(true);
        }
    }, [setToastMessage, setToastActive]);

    return (
      <Layout style={{ marginBottom: '24px' }}>
        <Layout.Section>
          <Card>
            {/* Filter/Status Tabs and Search Bar Row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 0 16px' }}>
              {/* Filter Tabs */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <ButtonGroup>
                  <Button
                    onClick={() => { setPreviewersStatusTab('all'); setPage(1); }}
                    variant="secondary"
                    tone={previewersStatusTab === 'all' ? 'success' : undefined}
                  >
                    All
                  </Button>
                  <Button
                    onClick={() => { setPreviewersStatusTab('completed'); setPage(1); }}
                    variant="secondary"
                    tone={previewersStatusTab === 'completed' ? 'success' : undefined}
                  >
                    Completed
                  </Button>
                  <Button
                    onClick={() => { setPreviewersStatusTab('pending'); setPage(1); }}
                    variant="secondary"
                    tone={previewersStatusTab === 'pending' ? 'success' : undefined}
                  >
                    Pending
                  </Button>
                </ButtonGroup>
                {/* Refresh Button */}
                <Button
                  icon={RefreshMajor}
                  onClick={fetchPreviewers}
                  variant="tertiary"
                  tone="base"
                  accessibilityLabel="Refresh previews"
                  style={{ marginLeft: 8 }}
                />
              </div>
              {/* Search Bar aligned right, styled like Stories/Scenes */}
              {previewers.length > 0 && (
                <div style={{ minWidth: 260 }}>
                  <TextField
                    label="Search by kid name, story, or generation ID..."
                    value={previewersSearchQuery}
                    onChange={(val) => {
                      setPage(1);
                      setPreviewersSearchQuery(val);
                    }}
                    placeholder="Search by kid name, story, or generation ID..."
                    autoComplete="off"
                    prefix={
                      <span className="Polaris-Icon">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="9" cy="9" r="7.5" stroke="var(--s-icon-color-25041, rgba(74, 74, 74, 1))" strokeWidth="1.5" />
                          <path d="M15 15L18 18" stroke="var(--s-icon-color-25041, rgba(74, 74, 74, 1))" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </span>
                    }
                    labelHidden
                  />
                </div>
              )}
            </div>
            
            <div style={{ overflowX: 'auto', marginTop: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                {previewers.filter(preview => {
                  // Filter by status tab
                  if (previewersStatusTab === 'completed' && preview.status !== 'completed') return false;
                  if (previewersStatusTab === 'pending' && preview.status === 'completed') return false;
                  // Search filter
                  const q = previewersSearchQuery.toLowerCase();
                  return (
                    (preview.kid_name && preview.kid_name.toLowerCase().includes(q)) ||
                    (preview.story_name && preview.story_name.toLowerCase().includes(q)) ||
                    (preview.story_slug && preview.story_slug.toLowerCase().includes(q)) ||
                    (preview.generation_id && preview.generation_id.toLowerCase().includes(q))
                  );
                }).length > 0 && (
                   <thead>
                    <tr>
                      {headings.map((heading, idx) => (
                        <th key={idx} style={{
                          textAlign: idx === 2 ? 'left' : 'center',
                          padding: '12px 8px',
                          fontWeight: 600,
                          color: '#6d7175',
                          fontSize: 13,
                          background: '#fafbfc',
                          borderBottom: '1px solid #e3e3e3',
                          minWidth: idx === 2 ? 56 : 80,
                        }}>{heading}</th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {isLoadingPreviewers ? (
                    <tr>
                      <td colSpan="7" style={{ padding: '60px 16px', textAlign: 'center' }}>
                        <BlockStack gap="400" align="center">
                          <Spinner size="large" />
                          <Text variant="bodyMd" tone="subdued">Loading previewers...</Text>
                        </BlockStack>
                      </td>
                    </tr>
                  ) : previewers.filter(preview => {
                    // Filter by status tab
                    if (previewersStatusTab === 'completed' && preview.status !== 'completed') return false;
                    if (previewersStatusTab === 'pending' && preview.status === 'completed') return false;
                    // Search filter
                    const q = previewersSearchQuery.toLowerCase();
                    return (
                      (preview.kid_name && preview.kid_name.toLowerCase().includes(q)) ||
                      (preview.story_name && preview.story_name.toLowerCase().includes(q)) ||
                      (preview.story_slug && preview.story_slug.toLowerCase().includes(q)) ||
                      (preview.generation_id && preview.generation_id.toLowerCase().includes(q))
                    );
                  }).length > 0 ? (
                    previewers.filter(preview => {
                      // Filter by status tab
                      if (previewersStatusTab === 'completed' && preview.status !== 'completed') return false;
                      if (previewersStatusTab === 'pending' && preview.status === 'completed') return false;
                      // Search filter
                      const q = previewersSearchQuery.toLowerCase();
                      return (
                        (preview.kid_name && preview.kid_name.toLowerCase().includes(q)) ||
                        (preview.story_name && preview.story_name.toLowerCase().includes(q)) ||
                        (preview.story_slug && preview.story_slug.toLowerCase().includes(q)) ||
                        (preview.generation_id && preview.generation_id.toLowerCase().includes(q))
                      );
                    }).map(preview => {
                      const kidImage = preview.kid_image || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-image.png';
                      const storyName = preview.story_name || (preview.story && preview.story.title ? preview.story.title : (preview.story_slug || ''));
                      const storyCover = (preview.generated_scenes && preview.generated_scenes[0] && preview.generated_scenes[0].processedImageUrl) || '';
                      const status = preview.status;
                      // In renderPreviewersContent, add a helper to format Cairo time
                      const formatCairoDate = (dateString) => {
                        if (!dateString) return { date: '-', time: '' };
                        try {
                          const date = new Date(dateString);
                          // Date: DD/MM/YYYY
                          const datePart = date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Africa/Cairo' });
                          // Time: HH:MM PM (uppercase)
                          let timePart = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Africa/Cairo' });
                          timePart = timePart.replace(/am|pm/i, match => match.toUpperCase());
                          return { date: datePart, time: timePart };
                        } catch {
                          return { date: '-', time: '' };
                        }
                      };
                      const cairoDateObj = formatCairoDate(preview.created_at);
                      return (
                        <tr key={preview._id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '12px 8px', minWidth: 120, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>{preview.generation_id}</td>
                          <td style={{ padding: '12px 8px', minWidth: 120, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>{preview.kid_name}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'left' }}>
                            <div 
                              style={{ 
                                cursor: 'pointer', 
                                width: 48, 
                                height: 48, 
                                borderRadius: 8, 
                                overflow: 'hidden', 
                                background: '#f6f6f7', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                transition: 'transform 0.2s ease-in-out'
                              }} 
                              onClick={() => handlePreviewImageClick(kidImage, preview.kid_name)}
                              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                            >
                              <AuthenticatedAssetImage
                                src={kidImage}
                                assetSize="thumbnail"
                                alt={preview.kid_name}
                                style={{ width: 48, height: 48, objectFit: 'cover' }}
                              />
                            </div>
                          </td>
                          <td style={{ padding: '12px 8px', minWidth: 120, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>{storyName}</td>
                          <td style={{ padding: '12px 8px', minWidth: 120, textAlign: 'center' }}>
                            {storyCover ? (
                              <div 
                                style={{ 
                                  cursor: 'pointer', 
                                  width: 48, 
                                  height: 48, 
                                  borderRadius: 8, 
                                  overflow: 'hidden', 
                                  background: '#f6f6f7', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center', 
                                  margin: '0 auto',
                                  transition: 'transform 0.2s ease-in-out'
                                }} 
                                onClick={() => handlePreviewImageClick(storyCover, storyName)}
                                onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                              >
                                <AuthenticatedAssetImage
                                  src={storyCover}
                                  assetSize="thumbnail"
                                  alt="Story Cover"
                                  style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8 }}
                                />
                              </div>
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 8px', minWidth: 140, color: '#6d7175', fontSize: 13, textAlign: 'center', lineHeight: 1.2 }}>
                            <span>{cairoDateObj.date}</span><br />
                            <span>{cairoDateObj.time}</span>
                          </td>
                          <td style={{ padding: '12px 8px', minWidth: 100, textAlign: 'center' }}>
                            <Badge tone={status === 'completed' ? 'success' : 'critical'}>
                              {status === 'completed' ? 'Completed' : 'Pending'}
                            </Badge>
                          </td>
                          <td style={{ padding: '12px 8px', minWidth: 120, textAlign: 'center' }}>
                            <InlineStack gap="100" align="center">
                              <Tooltip content="View">
                                <Button
                                  icon={ViewMajor}
                                  size="slim"
                                  onClick={() => {
                                    setSelectedPreview(preview);
                                    setIsViewPreviewModalOpen(true);
                                  }}
                                />
                              </Tooltip>
                              <Tooltip content="Copy share link">
                                <Button
                                  icon={ExternalMinor}
                                  size="slim"
                                  onClick={() => handleShareClick(preview.generation_id)}
                                />
                              </Tooltip>
                              <Tooltip content="Generate Story">
                                <Button
                                  icon={PlayMinor}
                                  size="slim"
                                  onClick={() => {
                                    setSelectedPreviewForGeneration(preview);
                                    setIsGenerateStoryModalOpen(true);
                                  }}
                                />
                              </Tooltip>
                            </InlineStack>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ padding: '60px 16px', textAlign: 'center' }}>
                        <BlockStack gap="400" align="center">
                          <Icon source={ContentMinor} color="subdued" className="empty-state-big-icon" />
                          <Text variant="bodyMd" tone="subdued">No records found</Text>
                        </BlockStack>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Segmented Pagination Controls (matching ScenesPage) */}
            {pageCount > 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '24px 0' }}>
                <ButtonGroup variant="segmented">
                  <Button
                    icon={ChevronLeftMinor}
                    disabled={!hasPrev || page === 1}
                    onClick={() => hasPrev && setPage(page - 1)}
                  />
                  {(() => {
                    let start = Math.max(1, page - 2);
                    let end = Math.min(pageCount, start + 4);
                    if (end - start < 4) start = Math.max(1, end - 4);
                    const pages = [];
                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <Button
                          key={i}
                          pressed={i === page}
                          onClick={() => setPage(i)}
                          variant={i === page ? 'secondary' : 'secondary'}
                        >
                          {i}
                        </Button>
                      );
                    }
                    return pages;
                  })()}
                  <Button
                    icon={ChevronRightMinor}
                    disabled={!hasNext || page === pageCount}
                    onClick={() => hasNext && setPage(page + 1)}
                  />
                </ButtonGroup>
                <div style={{ marginTop: 8, marginBottom: 16, color: '#6d7175', fontSize: 13 }}>
                  {total > 0 ? ((page - 1) * limit) + 1 : 0}
                  –
                  {total > 0 ? Math.min(((page - 1) * limit) + count, total) : 0}
                  {' of '}
                  {total}
                  {' (Page '}{page}{' of '}{pageCount || 1}{')'}
                </div>
              </div>
            )}
          </Card>
        </Layout.Section>

        {/* View Preview Modal */}
        <ViewPreviewModal
          isOpen={isViewPreviewModalOpen}
          onClose={() => setIsViewPreviewModalOpen(false)}
          selectedPreview={selectedPreview}
          setToastMessage={setToastMessage}
          setToastActive={setToastActive}
        />

        {/* Preview Image Modal */}
        <PreviewImageModal
          isOpen={isPreviewImageModalOpen}
          onClose={() => setIsPreviewImageModalOpen(false)}
          imageUrl={previewModalImageUrl}
          imageAlt={previewModalImageAlt}
        />

        {/* Create Previewer Modal */}
        <CreatePreviewerModal
          isOpen={isCreatePreviewerModalOpen}
          onClose={() => setIsCreatePreviewerModalOpen(false)}
          setToastMessage={setToastMessage}
          setToastActive={setToastActive}
          onPreviewerCreated={fetchPreviewers}
        />

        {/* Generate Story Confirm Modal */}
        <GenerateStoryConfirmModal
          isOpen={isGenerateStoryModalOpen}
          onClose={() => setIsGenerateStoryModalOpen(false)}
          preview={selectedPreviewForGeneration}
          setToastMessage={setToastMessage}
          setToastActive={setToastActive}
          onGenerated={fetchPreviewers}
        />
      </Layout>
    );
};

export default PreviewerContentPage;